import { useCallback, useState } from 'react'
import { flatten } from 'shared/util'

import { Button } from '@mui/material'

import { LockOpen as UnLockIcon } from '@mui/icons-material'
import { TrushIcon } from 'shared/icons'

import { UserResult, useEditUser, useDeleteUser, useResetPassword, useUnlockUser } from 'interface/user'
import { GroupResult } from 'interface/user/group'

import { UserForm, useUserForm } from '../form'

import { ConfirmDialog } from 'components/dialog/confirm'
import { MessageDialog } from 'components/dialog/message'
import { FormDialog } from 'components/dialog/form'

type EditUserDialogProps = {
  user: UserResult
  groupList: GroupResult[]
  onEditUser: (user: UserResult) => void
  onDeleteUser: (userId: number) => void
  onUnlockUser: (userId: number) => void
  onClose: () => void
}
export const EditUserDialog = ({
  user,
  groupList,
  onEditUser,
  onDeleteUser,
  onUnlockUser,
  onClose
}: EditUserDialogProps) => {
  const [modal, setModal] = useState<"deleteUser" | "resetPassword" | "resetedPassword" | "unlockUser" | null>(null)
  
  const onEditted = useCallback(
    (result: UserResult) => {
      onEditUser(result)
      onClose()
    },
    [onClose, onEditUser]
  )
  const [editUserState, editUser] = useEditUser(user.userId, { onEditted })
  const form = useUserForm({ groupList, user, onSubmit: editUser })
  
  const onDeleted = useCallback(
    (removedId: number) => {
      onDeleteUser(removedId)
      onClose()
    },
    [onClose, onDeleteUser]
  )
  const [deleteUserState, deleteUser] = useDeleteUser({ onDeleted })
  
  const onResetted = useCallback(
    () => {
      setModal("resetedPassword")
    },
    [setModal]
  )
  const [resetPasswordState, resetPassword] = useResetPassword({ onResetted })
  
  const onUnlocked = useCallback(
    (unlockedId: number) => {
      onUnlockUser(unlockedId)
      onClose()
    },
    [onClose, onUnlockUser]
  )
  const [unlockUserState, unlockUser] = useUnlockUser({ onUnlocked })
  
  return (
    <FormDialog
      open
      fullWidth
      maxWidth="md"
      title="ユーザー情報を編集"
      isLoading={editUserState.posting || deleteUserState.posting || resetPasswordState.posting || unlockUserState.posting}
      error={flatten([
        editUserState.error,
        deleteUserState.error,
        resetPasswordState.error,
        unlockUserState.error
      ])}
      body={<UserForm {...form} openResetPasswordConfirm={() => setModal("resetPassword")}/>}
      leftAction={
        <>
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<TrushIcon/>}
            onClick={() => setModal("deleteUser")}
          >
            削除
          </Button>

          {user.isLocked && (
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<UnLockIcon/>}
              onClick={() => setModal("unlockUser")}
            >
              ロック解除
            </Button>
          )}
        </>
      }
      submitLabel="保存"
      isValid={form.formState.isValid}
      onSubmit={form.submit}
      onClose={onClose}
    >
      {(modal === "deleteUser") ? (
        <ConfirmDialog
          isLoading={deleteUserState.posting}
          message={`${user.userName} を削除します。よろしいですか？`}
          ok={{
            label: 'はい',
            onClick: () => deleteUser(user.userId)
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => setModal(null)
          }}
        />
      ) : (modal === "resetPassword") ? (
        <ConfirmDialog
          isLoading={resetPasswordState.posting}
          message={`${user.userName} のパスワードをリセットします。よろしいですか？`}
          ok={{
            label: 'はい',
            onClick: () => resetPassword(user.userId)
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => setModal(null)
          }}
        />
      ) : (modal === "resetedPassword") ? (
        <MessageDialog
          message={`${user.userName} のパスワードをリセットしました`}
          onClose={() => setModal(null)}
        />
      ) : (modal === "unlockUser") ? (
        <ConfirmDialog
          isLoading={resetPasswordState.posting}
          message={`${user.userName} のロックを解除します。よろしいですか？`}
          ok={{
            label: 'はい',
            onClick: () => unlockUser(user.userId)
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => setModal(null)
          }}
        />
      ) : <></>}
    </FormDialog>
  )
}
